import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Search, RefreshCw, Loader2, AlertTriangle,
  Mail, Phone, Copy, Check, ChevronRight,
} from 'lucide-react';
import CompanyModal from '../components/CompanyModal';
import { searchCompanies, createCompany } from '../services/api';
import { useToast } from '../components/Toast';
import { FEEDBACK } from '../services/feedbackMessages';

const coerceList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.companies)) return raw.companies;
  if (raw && Array.isArray(raw.items)) return raw.items;
  return [];
};

const ErrorPanel = ({ error, onRetry }) => {
  const [copied, setCopied] = useState(false);
  const report = [
    `Error: ${error.message}`,
    `URL:   ${error.url || '(desconocida)'}`,
    `Status: ${error.status ?? '(sin respuesta HTTP)'}`,
    `Reason: ${error.reason || 'desconocido'}`,
    '',
    '--- Cuerpo de respuesta (primeros 400 chars) ---',
    error.bodySnippet || '(vacío)',
  ].join('\n');

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="border border-danger/30 bg-danger/5 rounded-xl p-5 mb-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-danger/15 text-danger flex items-center justify-center shrink-0">
          <AlertTriangle size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-danger font-bold mb-1">Error al llamar a la API</div>
          <div className="font-display text-base font-bold text-ink">{error.message}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={copyReport}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-ink border border-slate-200 hover:border-primary hover:text-primary bg-white transition-colors"
          >
            {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar informe</>}
          </button>
          {onRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-danger hover:bg-red-700 transition-colors"
            >
              <RefreshCw size={12} /> Reintentar
            </button>
          )}
        </div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs font-mono mt-4 pt-4 border-t border-danger/20">
        <dt className="text-ink-muted">URL</dt>
        <dd className="text-ink break-all">{error.url || '—'}</dd>
        <dt className="text-ink-muted">Status HTTP</dt>
        <dd className="text-ink">{error.status ?? 'sin respuesta'}</dd>
        <dt className="text-ink-muted">Causa</dt>
        <dd className="text-ink">{error.reason || 'desconocida'}</dd>
      </dl>
      {error.bodySnippet && (
        <div className="mt-3 pt-3 border-t border-danger/20">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Cuerpo de respuesta</div>
          <pre className="text-[11px] font-mono text-ink bg-white border border-slate-200 rounded-lg p-2 max-h-32 overflow-auto whitespace-pre-wrap break-all">{error.bodySnippet}</pre>
        </div>
      )}
      {error.reason === 'network' && (
        <div className="mt-3 text-xs text-ink-muted italic">
          Pista: un «Failed to fetch» suele significar CORS sin configurar, mixed-content (frontend HTTPS llamando a HTTP), o que el servidor no responde.
        </div>
      )}
      {error.reason === 'not-json' && (
        <div className="mt-3 text-xs text-ink-muted italic">
          Pista: el servidor contestó 200 pero con HTML. Probablemente el reverse-proxy está enrutando esta ruta a otra aplicación.
        </div>
      )}
    </div>
  );
};

const EmpresasPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const toast = useToast();

  const load = useCallback(async (q = '') => {
    setLoading(true);
    setError(null);
    try {
      const raw = await searchCompanies(q);
      setCompanies(coerceList(raw));
    } catch (err) {
      setError(err);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(query);
  }, [load, query]);

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery(inputValue.trim());
  };

  const clearSearch = () => {
    setInputValue('');
    setQuery('');
  };

  // Errores: CompanyModal los enseña dentro del modal (sigue abierto).
  const handleCreate = async (payload) => {
    await createCompany(payload);
    toast.success(FEEDBACK.companySaved({ name: payload.name, created: true }));
    await load(query);
  };

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="flex items-end justify-between mb-5 gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Administración</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">Empresas</h1>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors shadow-sm"
        >
          <Plus size={16} /> Alta empresa
        </button>
      </div>

      <form onSubmit={handleSearch} className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Buscar empresa por nombre…"
            className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm w-full"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-ink text-white hover:bg-slate-800 transition-colors"
        >
          Buscar
        </button>
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
          >
            Limpiar
          </button>
        )}
        <button
          type="button"
          onClick={() => load(query)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
          title="Recargar"
        >
          <RefreshCw size={14} />
        </button>
      </form>

      {error && <ErrorPanel error={error} onRetry={() => load(query)} />}

      <div className="border-t border-slate-200 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-16 flex flex-col items-center gap-2 text-ink-muted">
            <Loader2 size={24} className="animate-spin text-primary" />
            <div className="text-sm">Cargando empresas…</div>
          </div>
        ) : !error && companies.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-3 text-ink-muted">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Building2 size={22} className="text-ink-muted" />
            </div>
            <div className="text-sm">
              {query ? (
                <>No se encontraron empresas que coincidan con «<span className="font-semibold text-ink">{query}</span>».</>
              ) : (
                'Todavía no hay empresas registradas.'
              )}
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              <Plus size={14} /> Dar de alta la primera empresa
            </button>
          </div>
        ) : !error ? (
          <table className="w-full min-w-[720px]">
            <thead className="sticky top-0 bg-background z-10">
              <tr className="text-xs uppercase tracking-wider text-ink-muted">
                <th className="text-left font-semibold py-3 pr-4">Empresa</th>
                <th className="text-left font-semibold py-3 pr-4">CIF</th>
                <th className="text-left font-semibold py-3 pr-4 hidden md:table-cell">Responsable</th>
                <th className="text-left font-semibold py-3 pr-4 hidden lg:table-cell">Contacto</th>
                <th className="text-right font-semibold py-3 pr-4 w-16"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {companies.map((c, i) => (
                <tr
                  key={c.id ?? c.Id ?? c.cif ?? c.Cif ?? i}
                  onClick={() => navigate(`/panel/empresas/${c.id ?? c.Id}`)}
                  className="border-t border-slate-100 hover:bg-white transition-colors cursor-pointer"
                >
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-soft text-primary-darker flex items-center justify-center shrink-0">
                        <Building2 size={16} />
                      </div>
                      <div className="font-semibold text-ink">{c.name || c.Name || '—'}</div>
                    </div>
                  </td>
                  <td className="py-3 pr-4 tabular-nums font-mono text-xs text-ink">{c.cif || c.Cif || '—'}</td>
                  <td className="py-3 pr-4 text-ink hidden md:table-cell">{c.responsible || c.Responsible || '—'}</td>
                  <td className="py-3 pr-4 hidden lg:table-cell">
                    <div className="flex flex-col gap-0.5 text-xs">
                      {(c.email || c.Email) && (
                        <span className="inline-flex items-center gap-1.5 text-ink-muted">
                          <Mail size={11} /> {c.email || c.Email}
                        </span>
                      )}
                      {(c.phone || c.Phone) && (
                        <span className="inline-flex items-center gap-1.5 text-ink-muted">
                          <Phone size={11} /> {c.phone || c.Phone}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <ChevronRight size={16} className="text-ink-muted" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      <CompanyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
};

export default EmpresasPage;
